import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Camera, Globe, Palette, Briefcase, CalendarIcon, Clock, CheckCircle, ExternalLink, Users } from 'lucide-react';
import SITE_CONFIG from '@/config/siteConfig';

import { API } from '@/config/api';

// Services matching your website
const services = [
  {
    id: 'consultation',
    title: 'Business Consultation',
    description: 'Consultations are designed to give you clarity, strategy, and a personalized path forward. Click to book your session and start building with intention.',
    icon: Briefcase,
    price: 'Starting at $150/hour',
    includes: ['Business Strategy', 'Market Analysis', 'Action Plan', 'Follow-up Support'],
    image: 'https://static.wixstatic.com/media/b3a4967ff3214a43b9ca98e1b3b8777b.jpg'
  },
  {
    id: 'branding',
    title: 'Branding',
    description: 'Branding helps you define your vision, elevate your presence, and stand out with intention. Click to build a brand that aligns with who you are and where you\'re going.',
    icon: Palette,
    price: 'Starting at $500',
    includes: ['Logo Design', 'Brand Guidelines', 'Color Palette', 'Typography Selection', 'Brand Voice'],
    image: 'https://static.wixstatic.com/media/57e31dbf58d44095a1173a5517ba75bd.jpg'
  },
  {
    id: 'website',
    title: 'Website Design',
    description: 'Websites turn your vision into a digital home that works for you. Click to create a clean, intentional site that showcases your brand and drives results.',
    icon: Globe,
    price: 'Starting at $1,500',
    includes: ['Custom Design', 'Mobile Responsive', 'SEO Optimization', 'CMS Integration', 'Training'],
    image: 'https://static.wixstatic.com/media/11062b_31fcefa60a2e4659a556237badf702e1~mv2.jpg'
  },
  {
    id: 'photography',
    title: 'Photography & Fine Art',
    description: 'Photography services are crafted to capture your story with intention, depth, and luxury. Each shoot is designed to elevate your image, align with your brand, and turn visuals into currency that speaks power, presence, and legacy.',
    icon: Camera,
    price: 'Starting at $300/session',
    includes: ['Professional Shoot', 'Photo Editing', 'Digital Delivery', 'Print Rights', 'Creative Direction'],
    image: 'https://static.wixstatic.com/media/11062b_cdde939992964b469aafdcfb6e07bb55~mv2.jpg'
  },
  {
    id: 'artist',
    title: 'Artist Management',
    description: 'Artist memberships give you access to opportunities, exposure, and resources to grow your creative career with intention. Click to join and start building as an artist and a brand.',
    icon: Users,
    price: 'Contact for pricing',
    includes: ['Portfolio Review', 'Career Strategy', 'Exposure Opportunities', 'Resource Access', 'Community'],
    image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg'
  }
];

const timeSlots = [
  '9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
];

export default function MediaServices() {
  const { user, isAuthenticated, login } = useAuth();
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingTime, setBookingTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  
  const handleBookConsultation = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }
    
    if (!selectedService || !bookingDate || !bookingTime) {
      toast.error('Please select a service, date, and time');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/consultations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          service_type: selectedService,
          date: format(bookingDate, 'yyyy-MM-dd'),
          time: bookingTime,
          notes: notes
        })
      });
      
      if (response.ok) {
        setBookingSuccess(true);
        toast.success('Consultation request submitted! We will contact you shortly.');
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book consultation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleBookExternal = () => {
    window.open(SITE_CONFIG.brand.bookingLink, '_blank');
  };
  
  const resetBooking = () => {
    setBookingSuccess(false);
    setSelectedService(null);
    setBookingDate(null);
    setBookingTime('');
    setNotes('');
  };
  
  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-24 pb-12" data-testid="media-services">
      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-[#1c1814]">
        <div className="max-w-7xl mx-auto">
          <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37] mb-4 block">
            Creative Services
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FAFAFA] mb-4">
            Media <span className="text-[#D4AF37]">Services</span>
          </h1>
          <p className="font-body text-lg text-[#FAFAFA]/80 max-w-2xl mb-6">
            Elevate your brand with professional creative services. From branding to photography, 
            we help you build a visual presence that commands attention and drives results.
          </p>
          <Button 
            onClick={handleBookExternal}
            className="btn-gold"
            data-testid="book-external-btn"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Book on Official Site
          </Button>
        </div>
      </section>
      
      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                  selectedService === service.id 
                    ? 'border-2 border-[#D4AF37] shadow-lg' 
                    : 'border border-[#1c1814]/10 hover:border-[#D4AF37]/50'
                }`}
                onClick={() => setSelectedService(service.id)}
                data-testid={`service-${service.id}`}
              >
                {/* Service Image */}
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="p-3 bg-[#D4AF37]/10">
                      <service.icon className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    {selectedService === service.id && (
                      <CheckCircle className="h-6 w-6 text-[#D4AF37]" />
                    )}
                  </div>
                  <CardTitle className="font-display text-xl text-[#1c1814] mt-4">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="font-body text-[#1c1814]/60 line-clamp-3">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="font-display text-lg text-[#D4AF37] mb-4">{service.price}</p>
                  <ul className="space-y-2">
                    {service.includes.slice(0, 4).map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#1c1814]/70">
                        <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* Booking Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1c1814]">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-[#252540] border-[#D4AF37]/20" data-testid="booking-form">
            <CardHeader className="border-b border-[#D4AF37]/20">
              <CardTitle className="font-display text-2xl text-[#FAFAFA]">
                Book a <span className="text-[#D4AF37]">Consultation</span>
              </CardTitle>
              <CardDescription className="text-[#FAFAFA]/60">
                Select a service and schedule your consultation. You can also{' '}
                <button 
                  onClick={handleBookExternal}
                  className="text-[#D4AF37] hover:underline"
                >
                  book directly on our official site
                </button>.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {bookingSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-[#FAFAFA] mb-2">Request Submitted!</h3>
                  <p className="text-[#FAFAFA]/60 mb-6">
                    We'll contact you at {user?.email} with confirmation details.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={resetBooking} variant="outline" className="border-[#D4AF37] text-[#D4AF37]">
                      Book Another
                    </Button>
                    <Button onClick={handleBookExternal} className="btn-gold">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Book on Official Site
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Service Selection */}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-2 block">
                      Selected Service
                    </label>
                    <Select value={selectedService || ''} onValueChange={setSelectedService}>
                      <SelectTrigger className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA]">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1c1814] border-[#D4AF37]/30">
                        {services.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="text-[#FAFAFA]">
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date Selection */}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-2 block">
                      Preferred Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA] hover:bg-[#D4AF37]/10"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {bookingDate ? format(bookingDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1c1814] border-[#D4AF37]/30">
                        <Calendar
                          mode="single"
                          selected={bookingDate}
                          onSelect={setBookingDate}
                          disabled={(date) => date < new Date()}
                          className="bg-[#1c1814] text-[#FAFAFA]"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Time Selection */}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-2 block">
                      Preferred Time
                    </label>
                    <Select value={bookingTime} onValueChange={setBookingTime}>
                      <SelectTrigger className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA]">
                        <SelectValue placeholder="Select time">
                          <div className="flex items-center">
                            <Clock className="mr-2 h-4 w-4" />
                            {bookingTime || 'Select time'}
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-[#1c1814] border-[#D4AF37]/30">
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time} className="text-[#FAFAFA]">
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Notes */}
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-2 block">
                      Tell Us About Your Project
                    </label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="What are you looking to achieve? Any specific goals or requirements?"
                      className="bg-[#1c1814] border-[#D4AF37]/30 text-[#FAFAFA] min-h-[100px]"
                      data-testid="notes-input"
                    />
                  </div>
                  
                  {/* Submit */}
                  <Button 
                    onClick={handleBookConsultation}
                    disabled={submitting || !selectedService || !bookingDate || !bookingTime}
                    className="w-full btn-gold text-lg py-6"
                    data-testid="book-consultation-btn"
                  >
                    {submitting ? 'Submitting...' : isAuthenticated ? 'Submit Consultation Request' : 'Sign In to Book'}
                  </Button>
                  
                  <p className="text-center text-[#FAFAFA]/40 text-sm">
                    Or email us directly at{' '}
                    <a href={`mailto:${SITE_CONFIG.brand.email}`} className="text-[#D4AF37] hover:underline">
                      {SITE_CONFIG.brand.email}
                    </a>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
